import { LOAD_NUMBER } from 'config'
import { ChatRoomType, ConferenceCall, Message } from 'lib/_types'
import { yayApi } from '../baseApi'
import {
  pathApiChatRoomsAcceptChatRequest,
  pathApiChatRoomsAttachmentsRead,
  pathApiChatRoomsFindRoom,
  pathApiChatRoomsMainList,
  pathApiChatRoomsMassDestroy,
  pathApiChatRoomsMessages,
  pathApiChatRoomsMessagesDelete,
  pathApiChatRoomsMessagesNew,
  pathApiChatRoomsMessagesRead,
  pathApiChatRoomsNew,
  pathApiChatSingleRecipientNew,
  pathApiChatRoomsPinned,
  pathApiChatRoomsRequestList,
  pathApiChatRoomsShow,
  pathApiChatRoomsTotalChatRequest,
  pathApiChatRoomsVideosRead,
} from 'lib/paths'

export const chatApi = yayApi
  .enhanceEndpoints({
    addTagTypes: [
      'Chat',
      'ChatRoom',
      'ChatRooms',
      'ChatRoomsRequest',
      'TotalChatRequest',
      'Messages',
    ],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getHiddenChatRooms: build.query({
        providesTags: ['Chat'],
        query: ({ fromTimestamp, number = LOAD_NUMBER }) => ({
          url: `/v1/hidden/chats`,
          params: {
            from_timestamp: fromTimestamp,
            number,
          },
        }),
      }),

      getChatRoom: build.query<
        ChatRoomType,
        {
          chatId: number
        }
      >({
        providesTags: ['ChatRoom'],
        query: ({ chatId }) => ({
          url: pathApiChatRoomsShow({ chatId }),
        }),
        keepUnusedDataFor: 0,
        transformResponse: (response) => response?.chat,
      }),

      hideChatRoom: build.mutation({
        invalidatesTags: ['Chat'],
        query: (chatId) => ({
          url: `/v1/hidden/chats`,
          method: 'POST',
          data: {
            chat_room_id: chatId,
          },
        }),
      }),

      unhideChatRoom: build.mutation({
        invalidatesTags: ['Chat'],
        query: (chatId) => ({
          url: `/v1/hidden/chats`,
          method: 'DELETE',
          data: {
            chat_room_ids: chatId,
          },
        }),
      }),

      pinChatRoom: build.mutation({
        query: (chatId) => ({
          url: pathApiChatRoomsPinned({ chatId }),
          method: 'POST',
        }),
      }),

      unPinChatRoom: build.mutation({
        query: (chatId) => ({
          url: pathApiChatRoomsPinned({ chatId }),
          method: 'DELETE',
        }),
      }),

      getChatRoomsMainList: build.query<
        {
          chat_rooms: Array<ChatRoomType>
          next_page_value: number
          pinned_chats: Array<ChatRoomType>
        },
        {
          fromTimestamp: string
        }
      >({
        providesTags: ['ChatRooms'],
        query: ({ fromTimestamp }) => ({
          url: pathApiChatRoomsMainList,
          params: {
            number: LOAD_NUMBER,
            ...(fromTimestamp != '0'
              ? {
                  from_timestamp: fromTimestamp,
                }
              : {}),
          },
        }),
        // keepUnusedDataFor: 0,
      }),

      getRequestChatRoomList: build.query<
        {
          chat_rooms: Array<ChatRoomType>
          next_page_value: number
        },
        {
          fromTimestamp: string
        }
      >({
        providesTags: ['ChatRoomsRequest'],
        query: ({ fromTimestamp }) => ({
          url: pathApiChatRoomsRequestList,
          params: {
            number: LOAD_NUMBER,
            ...(fromTimestamp != '0'
              ? {
                  from_timestamp: fromTimestamp,
                }
              : {}),
          },
        }),
        keepUnusedDataFor: 0,
      }),

      getChatRoomsTotalChatRequest: build.query<
        {
          total: number
        },
        void
      >({
        providesTags: ['TotalChatRequest'],
        query: () => ({
          url: pathApiChatRoomsTotalChatRequest,
        }),
        keepUnusedDataFor: 0,
      }),

      acceptChatRequest: build.mutation<
        {},
        {
          chatRoomIds: Array<number>
        }
      >({
        invalidatesTags: ['ChatRoom'],
        query: ({ chatRoomIds }) => {
          const formData = new FormData()

          chatRoomIds.map((id) => {
            formData.set('chat_room_ids[]', id?.toString?.())
          })

          return {
            url: pathApiChatRoomsAcceptChatRequest,
            method: 'POST',
            data: formData,
          }
        },
      }),

      findChatRoomMutation: build.mutation<
        {
          chat_room_id: number
        },
        {
          userId: number
        }
      >({
        query: ({ userId }) => {
          return {
            url: pathApiChatRoomsFindRoom({ userId }),
          }
        },
      }),

      createSingleUserChat: build.mutation<
        {},
        {
          withUserId: number
          himaChat?: any
        }
      >({
        invalidatesTags: ['ChatRooms', 'TotalChatRequest'],
        query: ({ withUserId, himaChat }) => {
          return {
            url: pathApiChatSingleRecipientNew,
            method: 'POST',
            data: {
              ...(withUserId
                ? {
                    with_user_id: withUserId,
                  }
                : {}),
              ...(himaChat
                ? {
                    hima_chat: himaChat,
                  }
                : {}),
            },
          }
        },
      }),

      createChatRoom: build.mutation<
        {},
        {
          withUserIds?: Array<number>
          himaChat?: any
        }
      >({
        invalidatesTags: ['ChatRooms', 'TotalChatRequest'],
        query: ({ withUserIds, himaChat }) => {
          return {
            url: pathApiChatRoomsNew,
            method: 'POST',
            data: {
              ...(withUserIds
                ? {
                    with_user_ids: withUserIds,
                  }
                : {}),
              ...(himaChat
                ? {
                    hima_chat: himaChat,
                  }
                : {}),
            },
          }
        },
      }),

      deleteChatRoom: build.mutation<
        {},
        {
          chatRoomIds: Array<number>
        }
      >({
        invalidatesTags: ['TotalChatRequest'],
        query: ({ chatRoomIds }) => {
          return {
            url: pathApiChatRoomsMassDestroy,
            method: 'POST',
            data: {
              chat_room_ids: chatRoomIds,
            },
          }
        },
      }),

      getChatRoomMessages: build.query<
        {
          messages: Array<Message>
        },
        {
          chatId: number
          toMessageId?: number
          fromMessageId?: number
          aroundMessageId?: number
        }
      >({
        providesTags: ['Messages'],
        query: ({ chatId, toMessageId, fromMessageId, aroundMessageId }) => ({
          url: pathApiChatRoomsMessages({ chatId }),
          params: {
            number: LOAD_NUMBER,
            ...(toMessageId && { to_message_id: toMessageId }),
            ...(fromMessageId && {
              from_message_id: fromMessageId,
              reverse: true,
            }),
            ...(aroundMessageId && { around_message_id: aroundMessageId }),
          },
        }),
        keepUnusedDataFor: 0,
      }),

      readChatRoomMessages: build.mutation<
        {},
        {
          chatId: number
          messageId: number
        }
      >({
        query: ({ chatId, messageId }) => {
          return {
            url: pathApiChatRoomsMessagesRead({ chatId, messageId }),
            method: 'POST',
          }
        },
      }),

      deleteChatRoomMessages: build.mutation<
        {},
        {
          chatId: number
          messageId: number
        }
      >({
        query: ({ chatId, messageId }) => {
          return {
            url: pathApiChatRoomsMessagesDelete({ chatId, messageId }),
            method: 'DELETE',
          }
        },
      }),

      readAttachment: build.mutation<
        {},
        {
          chatId: number
          ids: Array<number>
        }
      >({
        query: ({ chatId, ids }) => {
          return {
            url: pathApiChatRoomsAttachmentsRead({ chatId }),
            method: 'POST',
            data: {
              attachment_msg_ids: ids,
            },
          }
        },
      }),

      readVideo: build.mutation<
        {},
        {
          chatId: number
          ids: Array<number>
        }
      >({
        query: ({ chatId, ids }) => {
          return {
            url: pathApiChatRoomsVideosRead({ chatId }),
            method: 'POST',
            data: {
              video_msg_ids: ids,
            },
          }
        },
      }),

      createMessage: build.mutation<
        {
          id: number
          result: 'success'
          conference_call?: ConferenceCall
        },
        {
          chatId: string
          data: {
            message_type: string
            text?: string
            sticker_pack_id?: number
            sticker_id?: number
            gif_image_id?: number
            call_type?: string
            attachment_file_name?: string
            parent_id?: number
          }
        }
      >({
        query: ({ chatId, data }) => {
          return {
            url: pathApiChatRoomsMessagesNew({ chatId }),
            method: 'POST',
            data,
          }
        },
      }),
    }),
    overrideExisting: false,
  })

export const {
  useGetHiddenChatRoomsQuery,
  useGetChatRoomQuery,
  useHideChatRoomMutation,
  useUnhideChatRoomMutation,
  usePinChatRoomMutation,
  useUnPinChatRoomMutation,
  useGetChatRoomsMainListQuery,
  useGetRequestChatRoomListQuery,
  useGetChatRoomsTotalChatRequestQuery,
  useAcceptChatRequestMutation,
  useFindChatRoomMutationMutation,
  useCreateSingleUserChatMutation,
  useCreateChatRoomMutation,
  useDeleteChatRoomMutation,
  useGetChatRoomMessagesQuery,
  useReadChatRoomMessagesMutation,
  useDeleteChatRoomMessagesMutation,
  useReadAttachmentMutation,
  useReadVideoMutation,
  useCreateMessageMutation,
} = chatApi
